import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { matchingAppointmentTypes } from '@/lib/appointmentTypeSearch';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export type AppointmentTypeChoice = {
  id: string;
  name: string;
  durationMinutes: number;
};

/**
 * Aftaletype on Ny aftale. Same search as the patient field on that form:
 * focus opens a list under the field, typing filters it, and one row is the
 * choice. The caller passes patient appointment types only (no blokeringer).
 */
export function AppointmentTypeTypeahead({
  types,
  value,
  onChange,
  label,
  placeholder,
  hint,
  changeLabel,
  emptyLabel,
  minutesShort,
  disabled = false,
}: {
  types: readonly AppointmentTypeChoice[];
  value: string | null;
  onChange: (id: string | null) => void;
  label: string;
  placeholder: string;
  hint?: string;
  changeLabel: string;
  emptyLabel: string;
  minutesShort: string;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = types.find((type) => type.id === value) ?? null;
  const matches = useMemo(() => matchingAppointmentTypes(types, search), [types, search]);
  const showEmpty = open && search.trim().length > 0 && matches.length === 0;

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function durationLabel(type: AppointmentTypeChoice): string {
    return `${type.durationMinutes} ${minutesShort}`;
  }

  if (selected) {
    return (
      <Field
        label={label}
        htmlFor="appointment-service"
        required
        hint={hint}
        hintId="appointment-service-hint"
      >
        <div
          className="flex items-center gap-s3 rounded-pop border border-ds-line bg-ds-bg px-s3 py-s2"
          data-testid="appointment-type-selected"
        >
          <div className="min-w-0 flex-1">
            <p className={cn(TYPE.section, 'truncate text-ds-text')}>{selected.name}</p>
            <p className={cn(TYPE.small, 'mt-0.5 truncate text-ds-muted')}>
              {durationLabel(selected)}
            </p>
          </div>
          {!disabled ? (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={() => {
                setSearch('');
                setOpen(false);
                onChange(null);
              }}
            >
              {changeLabel}
            </Button>
          ) : null}
        </div>
      </Field>
    );
  }

  return (
    <Field
      label={label}
      htmlFor="appointment-service"
      required
      hint={hint}
      hintId="appointment-service-hint"
    >
      <div ref={wrapperRef} className="relative" data-testid="appointment-type-search">
        <Input
          id="appointment-service"
          leadingIcon={<Search className="h-4 w-4" />}
          value={search}
          disabled={disabled}
          autoComplete="off"
          placeholder={placeholder}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-required="true"
          aria-describedby="appointment-service-hint"
          onChange={(event) => {
            setSearch(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (!disabled) setOpen(true);
          }}
        />
        {open && (matches.length > 0 || showEmpty) ? (
          <ul
            id={listId}
            data-testid="appointment-type-typeahead"
            className="absolute z-10 mt-s1 max-h-60 w-full overflow-auto rounded-pop border border-ds-line bg-ds-card py-s1 shadow-pop"
          >
            {matches.map((type) => (
              <li key={type.id}>
                <button
                  type="button"
                  data-testid="appointment-type-option"
                  data-appointment-type-id={type.id}
                  onClick={() => {
                    onChange(type.id);
                    setSearch('');
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-s3 px-s3 py-s2 text-left hover:bg-ds-teal-tint"
                >
                  <span className="min-w-0">
                    <span className={cn(TYPE.section, 'block truncate text-ds-text')}>
                      {type.name}
                    </span>
                    <span className={cn(TYPE.small, 'block truncate text-ds-muted')}>
                      {durationLabel(type)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
            {showEmpty ? (
              <li>
                <p
                  className={cn(TYPE.small, 'px-s3 py-s2 text-ds-muted')}
                  data-testid="appointment-type-search-empty"
                >
                  {emptyLabel}
                </p>
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </Field>
  );
}
